// ===========================================
// Export API
// ===========================================
// API client for school data export

import { apiClient } from '../services/api';

const BASE_URL = '/admin/export';

// ===========================================
// TYPES
// ===========================================
// Keep in sync with apps/backend/src/types/export.types.ts

export const EXPORT_ENTITY_KEYS = [
  'students',
  'families',
  'teachers',
  'lessons',
  'attendance',
  'terms',
  'invoices',
  'payments',
  'instruments',
  'programLevels',
  'locations',
  'rooms',
  'lessonTypes',
  'lessonDurations',
] as const;

export type ExportEntityKey = (typeof EXPORT_ENTITY_KEYS)[number];

export type ExportFormat = 'csv' | 'json';

export interface ExportParams {
  entities: ExportEntityKey[];
  format: ExportFormat;
}

// ===========================================
// API FUNCTION
// ===========================================

/**
 * Download exported school data as CSV/JSON/ZIP.
 * Triggers a browser file download.
 */
export async function downloadExport(params: ExportParams): Promise<void> {
  const queryString = new URLSearchParams({
    entities: params.entities.join(','),
    format: params.format,
  }).toString();

  const response = await apiClient.getRaw<Blob>(`${BASE_URL}?${queryString}`, {
    responseType: 'blob',
  });

  // Extract filename from Content-Disposition header or build a default
  const disposition = response.headers['content-disposition'] as string | undefined;
  const isMultiCsv = params.format === 'csv' && params.entities.length > 1;
  const defaultExt = params.format === 'json' ? 'json' : isMultiCsv ? 'zip' : 'csv';
  let filename = `export.${defaultExt}`;
  if (disposition) {
    const match = disposition.match(/filename="?([^";\s]+)"?/);
    if (match) filename = match[1];
  }

  // Trigger browser download
  const blob = new Blob([response.data]);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
